export enum OrderState {
  Pending = "Pending",
  Preorder = "Preorder",
  InProgress = "InProgress",
  Ready = "Ready",
  Delivering = "Delivering",
  Delivered = "Delivered",
  Cancelled = "Cancelled",
  Rejected = "Rejected",
}

export enum PaymentPlatform {
  Web = "web",
  Mobile = "mobile",
}

export interface EligiblePaymentMethodDto {
  id: string;
  code: string;
  name: string;
  description: string;
  isEligible: boolean;
  eligibilityMessage: string | null;
  customFields: {
    supportedPlatforms: PaymentPlatform[] | null;
  } | null;
}

export type PaymentFlowType = "offline" | "sheet" | "redirect";

export interface StripeSheetConfig {
  publishableKey: string;
  stripeAccountId: string | null;
  paymentIntentClientSecret: string;
  customerId: string | null;
  ephemeralKeySecret: string | null;
  merchantDisplayName: string | null;
  redirectUrl?: string;
}

export interface PaymentSession {
  sessionId: string | null;
  provider: string;
  flowType: PaymentFlowType;
  status: string;
  clientConfig: StripeSheetConfig | null;
}

export interface OrderTotals {
  subTotal: number;
  subTotalWithTax: number;
  shipping: number;
  shippingWithTax: number;
  total: number;
  totalWithTax: number;
}

export interface OrderDiscount {
  description: string;
  label: string;
  couponCode: string | null;
  amount: number;
  amountWithTax: number;
  type: string;
  adjustmentSource: string;
}

export interface OrderDto {
  id: string;
  code: string;
  state: string;
  status: string | null;
  active: boolean;
  orderPlacedAt: string | null;
  currencyCode: string;
  preorder: boolean;
  scheduledFor: string | null;
  totals: OrderTotals;
  couponCodes: string[];
  discounts: OrderDiscount[];
}

export interface PlaceOrderResponse {
  order: OrderDto;
  payment: PaymentSession | null;
}

export type PaymentFlowOutcome =
  | { status: "success"; order: OrderDto }
  | { status: "pending" }
  | { status: "incomplete" }
  | { status: "error"; message?: string }
  | { status: "aborted" };

export const PENDING_PAYMENT_KEY = "munchi:pending-payment";

export interface PendingPayment {
  sessionId: string;
  channelToken: string;
}

export interface FlushSelectionItem {
  subOptionId: number;
  quantity: number;
}

export interface FlushSelection {
  optionId: number;
  items: FlushSelectionItem[];
}

export interface FlushCartItem {
  productId: number;
  quantity: number;
  comment?: string;
  selections: FlushSelection[];
  removedIngredientIds?: number[];
}

export enum IngredientRemovalRefusal {
  OFF = "INGREDIENT_REMOVAL_OFF",
  NOT_REMOVABLE = "INGREDIENT_NOT_REMOVABLE",
}

export const ORDER_SOURCE = "web";

export interface FlushCartContext {
  type?: number | null;
  pickupTime?: string | null;
  supportLabel?: string | null;
  scheduledFor?: string | number | null;
  supportType?: string | null;
  supportNumber?: string | null;
  specialInstructions?: string | null;
  comment?: string | null;
  orderSource?: string;
}

export interface FlushDeliveryAddress {
  streetLine1: string;
  streetLine2: string | null;
  city: string | null;
  postalCode: string | null;
  countryCode: string | null;
  fullName: string | null;
  locationType: number | null;
  entrance: string | null;
  floor: string | null;
  instructions: string | null;
  latitude?: number;
  longitude?: number;
}

export interface FlushCartRequest {
  items: FlushCartItem[];
  context?: FlushCartContext;
  deliveryAddress?: FlushDeliveryAddress;
}

export interface AttachCustomerPayload {
  emailAddress?: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  phoneNumber?: string;
}

export interface PlaceOrderRequest {
  method: string;
  metadata?: Record<string, string>;
  orderSource?: string;
}

export interface OrderChangeEvent {
  orderId: string;
  orderNumber: string;
  status: string;
  orderType?: string;
  pickUpEta?: string;
  preparedIn?: number;
  acceptedAt?: string;
  timestamp?: string;
}

export interface OrderDetailSeller {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  companyName: string;
}

export interface OrderDetailContext {
  comment: string | null;
  preorder: boolean;
  pickupTime: string | null;
  deliveredAt: string | null;
  serviceMode: "delivery" | "takeaway" | "pickup" | "dine_in";
  supportType: string | null;
  scheduledFor: string | null;
  supportLabel: string | null;
  supportNumber: string | null;
  deliveryStatus: string | null;
  specialInstructions: string | null;
  deliveryProviderType: string | null;
}

export interface OrderDetailItemPricing {
  lineTax: number;
  taxRate: number;
  unitTax: number;
  linePrice: number;
  unitPrice: number;
  baseUnitPrice: number;
  linePriceWithTax: number;
  unitPriceWithTax: number;
  unitPriceBeforeDiscounts: number;
}

export interface OrderDetailItem {
  id: string;
  sku: string;
  choices: unknown[];
  comment: string | null;
  pricing: OrderDetailItemPricing;
  quantity: number;
  productName: string;
  variantName: string;
  productVariantId: string;
}

export interface OrderDetailPayment {
  state: string;
  amount: number;
  method: string;
  provider: string | null;
  transactionId: string;
}

export interface OrderDetailDeliveryAddress {
  streetLine1: string;
  streetLine2: string | null;
  city: string | null;
  postalCode: string | null;
  countryCode: string | null;
  instructions: string | null;
}

export interface OrderDetailFeePart {
  type: string;
  total: { amount: number; currency: string };
  vat_percentage: number;
}

export interface OrderDetailFees {
  parts: OrderDetailFeePart[];
}

export interface OrderDetailDto {
  id: string;
  code: string;
  state: string;
  status: string;
  active: boolean;
  preorder: boolean;
  seller: OrderDetailSeller;
  context: OrderDetailContext;
  items: OrderDetailItem[];
  totals: OrderTotals;
  fees: OrderDetailFees | null;
  payment: OrderDetailPayment;
  deliveryAddress: OrderDetailDeliveryAddress | null;
  currencyCode: string;
  scheduledFor: string | null;
  pickupTime: string | null;
  createdAt: string;
  updatedAt: string;
  orderPlacedAt: string | null;
  sellerId: string;
}

export interface PaginatedOrdersDto {
  data: OrderDetailDto[];
  total: number;
  page: number;
  pageSize: number;
}
